import { memo, useState, useCallback, useMemo, type ChangeEvent, type KeyboardEvent } from 'react';
import { toast } from 'react-toastify';
import {
  DomainRegisteredStatus,
  DomainNotRegisteredStatus,
  DomainUnknownStatus,
  DomainConflictStatus,
} from './DomainStatus';
import { ContainerWithBackButton } from '~/components/dialogs/settings/ContainerWithBackButton';
import { DialogActions, DialogButton } from '~/components/ui/Dialog';
import { Input } from '~/components/ui/Input';
import { Link } from '~/components/ui/Link';
import { addDomainToProject, verifyDomain } from '~/lib/.client/api/domains';
import { importStore } from '~/lib/projects';
import { domainsStore } from '~/lib/stores/domains';
import { projectStore } from '~/lib/stores/project';
import { HELP_CENTER_DOMAINS } from '~/lib/urls';
import { invariant } from '~/utils/invariant';
import { preventUserInteractions } from '~/utils/overlay';
import { withSpinner } from '~/utils/withSpinner';

interface AddCustomDomainProps {
  onClose: VoidFunction;
}

export const AddCustomDomain = memo(({ onClose }: AddCustomDomainProps) => {
  const [loading, setLoading] = useState(false);

  const [domain, setDomain] = useState('');
  const [domainSubmitted, setDomainSubmitted] = useState('');
  const [domainStatus, setDomainStatus] = useState<'valid' | 'non-existent' | 'conflict' | 'unknown' | undefined>();

  const onVerify = useCallback(
    async () =>
      withSpinner({
        action: async () => {
          if (!domain.trim()) {
            toast.error('Please enter a domain name.');
            return;
          }

          setDomainStatus(undefined);

          const reenable = preventUserInteractions();

          try {
            const status = await verifyDomain(domain);

            setDomainStatus(status);
            setDomainSubmitted(domain);
          } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to verify domain.');
          } finally {
            reenable();
          }
        },
        onStateUpdate: setLoading,
      }),
    [domain],
  );

  const onDomainChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setDomain(value);
    setDomainStatus(undefined);
    setDomainSubmitted('');
  }, []);

  const handleInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && !loading && domain.trim()) {
        onVerify();
      }
    },
    [onVerify, loading, domain],
  );

  const buyDomain = useCallback(() => {
    domainsStore.domainSearchQuery.set(domainSubmitted);
    domainsStore.domainSearchResult.set(undefined);

    domainsStore.setBuyNewDomainStep();
  }, [domainSubmitted]);

  const addDomain = useCallback(
    async () =>
      withSpinner({
        action: async () => {
          // TODO: Implement add domain
          const reenable = preventUserInteractions();

          try {
            const projectId = projectStore.projectId.get();

            invariant(projectId);

            const { siteUrl, entries } = await addDomainToProject(domainSubmitted, projectId);

            domainsStore.domainEntries.set(entries);

            const deploymentInfo = importStore.get().deploymentInfo;

            if (deploymentInfo) {
              // update the deployment info with the new site URL so that the publish menu reflects the new domain
              importStore.setKey('deploymentInfo', {
                ...deploymentInfo,
                site_url: siteUrl,
                is_custom_domain: true,
              });
            }

            domainsStore.resetStep();
          } catch (error: any) {
            if (error.code === 422) {
              // this means the domain is already attached to another project
              setDomainStatus('conflict');

              return;
            }

            toast.error(error instanceof Error ? error.message : 'Failed to add domain.');
          } finally {
            reenable();
          }
        },
        onStateUpdate: setLoading,
      }),
    [domainSubmitted],
  );

  const buttonConfig = useMemo(() => {
    switch (domainStatus) {
      case 'valid': {
        return {
          onClick: addDomain,
          children: loading ? (
            <>
              <span className="i-svg-spinners:90-ring-with-bg size-4 inline-block" />
              Adding...
            </>
          ) : (
            <>Add domain</>
          ),
        };
      }
      case 'non-existent': {
        return {
          onClick: buyDomain,
          children: 'Buy this domain',
        };
      }
      default: {
        return {
          onClick: onVerify,
          disabled: loading || !domain.trim(),
          className: 'flex items-center gap-2',
          children: loading ? (
            <>
              <span className="i-svg-spinners:90-ring-with-bg size-4 inline-block" />
              Verifying...
            </>
          ) : (
            <>Verify</>
          ),
        };
      }
    }
  }, [domainStatus, loading, domain, addDomain, buyDomain, onVerify]);

  return (
    <ContainerWithBackButton className="gap-6" title="Add your custom domain" onClose={onClose}>
      <p className="text-bolt-ds-textSecondary">
        Connect a domain you own to your deployed environment. Click{' '}
        <Link href={`${HELP_CENTER_DOMAINS}#connect-your-domain`} target="_blank" className="decoration-none">
          here
        </Link>{' '}
        to learn more.
      </p>

      {domainStatus === 'valid' && <DomainRegisteredStatus domain={domainSubmitted} />}
      {domainStatus === 'non-existent' && <DomainNotRegisteredStatus domain={domainSubmitted} />}
      {domainStatus === 'unknown' && <DomainUnknownStatus />}
      {domainStatus === 'conflict' && <DomainConflictStatus />}

      <div className="self-stretch flex flex-col mt-2">
        <Input
          inputClassName="bg-bolt-ds-surface"
          type="text"
          value={domain}
          label="Custom domain"
          onChange={onDomainChange}
          onKeyDown={handleInputKeyDown}
          placeholder="yourdomain.com"
        />
      </div>

      <DialogActions className="p-0 self-stretch justify-between items-center ml-auto">
        <DialogButton size="md" variant="primary" truncate={false} {...buttonConfig} />
      </DialogActions>
    </ContainerWithBackButton>
  );
});

AddCustomDomain.displayName = 'AddCustomDomain';
